import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService, IAuthServiceStatusSearchFilters, IAuthStatusSearchFilters } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AuthServiceStatusReasonReloadEvent, AuthServiceStatusReloadEvent } from 'app/um/lib/events';
import { localSort } from 'app/shared/lib/sorting';
import { I18N } from 'aurelia-i18n';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveServiceStatus } from 'app/um/components/service-statuses/save-service-status';
import { deepClone } from 'app/shared/lib/utilities';
import { AuthContext } from 'app/um/lib/auth-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import * as UM from 'app/um/models/essette-api';

export enum FlagOptions {
    isApproval = 'Is Approval',
    isOpen = 'Is Open',
    isTurnAroundTimeBase = 'Is TAT Base',
    isVoided = 'Is Voided',
    sendToClaimsSystem = 'Send to Claims System'
}

enum StatusEvents {
    isInitial = 'Initial Status',
    providerChangesRequested = 'Changes Requested by Provider'
}

interface CustomAuthServiceStatus extends UM.AuthServiceStatus {
    // Values For Grid Column Sorting
    flagValue?: string | null;
    transitionValue?: string | null;
    eventValue?: string | null;
    // Display For Grid Column Display
    flagDisplay?: string | null;
    transitionDisplay?: string | null;
    eventDisplay?: string | null;
    // Tooltip list For Grid Column
    flagToolTipDesc?: string | null;
    transitionToolTipDesc?: string | null;
    statusEventToolTipDesc?: string | null;
}

@autoinject()
export class ServiceStatuses extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    protected authServiceStatuses: CustomAuthServiceStatus[] = [];
    protected allAuthServiceStatuses: CustomAuthServiceStatus[] = [];
    protected editedAuthServiceStatus: UM.AuthServiceStatus | null = null;
    protected determinationStatuses: UM.AuthStatus[] = [];
    protected flagOptions: string[] = [
        FlagOptions.isApproval,
        FlagOptions.isOpen,
        FlagOptions.isTurnAroundTimeBase,
        FlagOptions.isVoided
    ];
    protected allFlagOptions: string[] = [
        FlagOptions.isApproval,
        FlagOptions.isOpen,
        FlagOptions.isTurnAroundTimeBase,
        FlagOptions.isVoided,
        FlagOptions.sendToClaimsSystem
    ];
    protected determinationFlags: string[] = [
        FlagOptions.sendToClaimsSystem
    ];

    public searchFilters: IAuthServiceStatusSearchFilters = {
        determination: {
            statuses: []
        },
        status: '',
        transitions: []
    };

    private apiSorting: Sorting = new Sorting();
    private defaultApiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['flagValue', 'transitionValue', 'eventValue'];
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////

    protected $pager!: EssPagerCustomElement;

    // Save Status Modal
    protected $modal!: IPopup;
    protected $form!: IForm;
    protected $saveServiceStatus!: SaveServiceStatus;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly context: AuthContext,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);

        this.defaultApiSorting.add({ reference: 'description', direction: SortDirection.Ascending });
        this.defaultApiSorting.add({ reference: 'flag.providerChangesRequested', direction: SortDirection.Descending });
        this.defaultApiSorting.add({ reference: 'flag.isInitial', direction: SortDirection.Descending });
    }

    protected bind(): void {
        this.reload(true);
        this.loadLookups();
    }

    protected async attached(): Promise<void> {
        // check if Sync to Claims System configured for tenant for total flag count
        await this.context.loadBasicSettings();
        const syncToClaimsSystemLineLevelEnabled = !!getApiSetting(this.context.apiSettings, 'syncToClaimsSystemLineLevel.$isEnabled');
        const lineLevelTATEnabled = !!getApiSetting(this.context.apiSettings, 'lineLevelTAT.$isEnabled');
        if(!syncToClaimsSystemLineLevelEnabled) {
            this.flagOptions = this.flagOptions.filter(flag => flag !== FlagOptions.sendToClaimsSystem);
            this.allFlagOptions = this.allFlagOptions.filter(flag => flag !== FlagOptions.sendToClaimsSystem);
            this.determinationFlags = this.determinationFlags.filter(flag => flag !== FlagOptions.sendToClaimsSystem);
        }
        if(!lineLevelTATEnabled) {
            this.flagOptions = this.flagOptions.filter(flag => flag !== FlagOptions.isTurnAroundTimeBase);
            this.allFlagOptions = this.allFlagOptions.filter(flag => flag !== FlagOptions.isTurnAroundTimeBase);
        }
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { localSorting, apiSorting } = this;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const authServiceStatuses: CustomAuthServiceStatus[] = <CustomAuthServiceStatus[]>this.authServiceStatuses;
            if(direction === null) {
                localSorting.remove(property);
                // if no local sorts, revert to API sorts by reloading
                if(localSorting.entries.length === 0) this.reload();
            } else {
                localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(authServiceStatuses, localSorting);
        }
        // Else If API Sort
        else {
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            await this.reload();
        }
    }

    ////****************** Functions ************************************************////
    public async reload(refreshAllStatuses: boolean = false): Promise<void> {
        await this.loadAuthServiceStatuses(refreshAllStatuses);
    }

    protected saveAuthServiceStatus(authStatus: UM.AuthServiceStatus): void {
        this.editedAuthServiceStatus = deepClone(authStatus);
        this.$modal.open();
    }

    protected saveAuthServiceStatusClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
    }

    // Set Custom Display
    protected setAuthServiceStatusDetails = (authServiceStatuses: CustomAuthServiceStatus[], allActiveCount: number = 0): CustomAuthServiceStatus[] => {
        authServiceStatuses.forEach(status => {
            // transform flag data
            const flags = Object.entries({ ...status.flag })    // convert object into array of key-value pairs
                .filter(f => f[1] && f[0] in FlagOptions)       // filter out inapplicable flags and event flags
                .map(f => {                                     // regex to convert camelCased flag keys to Regular Case
                    return f[0].replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, c => c.toUpperCase());
                })
                .sort((a, b) => a.localeCompare(b));            // sort alphabetically
            if(flags.length) {
                const flagCount = flags.length;
                const totalFlagCount = this.allFlagOptions.length;
                if(flagCount === totalFlagCount) { // if all possible flags are set
                    status.flagValue = status.flagDisplay = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('um:auth.status.flag.system.$long', { count: flagCount }) });
                } else if(flagCount === 1) {
                    status.flagValue = status.flagDisplay = flags[0];
                } else {
                    status.flagDisplay = status.flagValue = flagCount.toString() + ' ' + this.i18n.tr('um:auth.status.flag.system.$long', { count: flagCount });
                    let listDisplay = '<ul>';
                    flags.forEach(f => {
                        listDisplay += '<li>' + f + '</li>';
                    });
                    listDisplay += '</ul>';

                    status.flagToolTipDesc = listDisplay;
                }
            } else {
                status.flagDisplay = status.flagValue = "None";
            }

            // transform transition data
            const transitions = status.transitions || [];
            if(transitions.length) {
                const transitionCount = transitions.length;
                if(transitionCount === allActiveCount) { // if all statuses are transitions
                    status.transitionValue = status.transitionDisplay = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('um:auth.status.transition.$long', { count: transitionCount }) });
                } else if(transitionCount === 1) {
                    status.transitionValue = status.transitionDisplay = transitions[0].description;
                } else {
                    status.transitionDisplay = status.transitionValue = transitionCount.toString() + ' ' + this.i18n.tr('um:auth.status.transition.$', { count: transitionCount });
                    let listDisplay = '<ul>';
                    transitions.forEach(t => {
                        listDisplay += '<li>' + t.description + '</li>';
                    });
                    listDisplay += '</ul>';

                    status.transitionToolTipDesc = listDisplay;
                }
            } else {
                status.transitionDisplay = status.transitionValue = this.i18n.tr('ui:labels.none');
            }

            // transform event data
            const events = Object.entries({ ...status.flag })    // convert object into array of key-value pairs
                .filter(f => f[1] && f[0] in StatusEvents)       // filter out inapplicable flags and event flags
                .map(f => {                                     // regex to convert camelCased flag values to Regular Case
                    return f[0] = this.i18n.tr(`um:service.status.flag.${f[0]}.$`).replace(/\b\w/g, c => c.toUpperCase());
                })
                .sort((a, b) => a.localeCompare(b));            // sort alphabetically
            if(events.length) {
                const eventCount = events.length;
                if(eventCount === 1) {
                    status.eventValue = status.eventDisplay = events[0];
                } else {
                    status.eventDisplay = status.eventValue = eventCount.toString() + ` ${this.i18n.tr('um:service.status.event.$long_plural')}`;
                    let listDisplay = '<ul>';
                    events.forEach(f => {
                        listDisplay += '<li>' + f + '</li>';
                    });
                    listDisplay += '</ul>';

                    status.statusEventToolTipDesc = listDisplay;
                }
            } else {
                status.eventDisplay = status.eventValue = "";
            }
        });

        return authServiceStatuses;
    }

    protected async formSuccess(): Promise<void> {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
        this.events.publish(new AuthServiceStatusReasonReloadEvent());
        await this.reload(true);
    }

    protected getResult(status: UM.AuthStatus | undefined) {
        return status ? status.determination!.result : 'none';
    }

    protected async clearResults(): Promise<void> {
        this.searchFilters = {
            determination: {
                statuses: []
            },
            status: '',
            transitions: []
        };
        await this.loadAuthServiceStatuses();
    }

    protected getIndicatorCount(status: CustomAuthServiceStatus): number {
        let count = 0;
        if(status.flag) {
            if(status.flag.isInitial) count++;
            if(status.flag.providerChangesRequested) count++;
        }
        return count;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    protected async activateAuthServiceStatus(authServiceStatus: UM.AuthServiceStatus): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(authServiceStatus.isActive) {
            const result = await confirmation.showDeactivate('um:service.status.$', 1);
            if(!(result.dialogResponse === confirmation.options.OK)) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAuthServiceStatus(authServiceStatus.id!, !authServiceStatus.isActive);
            isSuccessful = response.isSuccessful;
            this.events.publish(new AuthServiceStatusReloadEvent());
            this.events.publish(new AuthServiceStatusReasonReloadEvent());
            this.reload(true);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:service.status.$', !authServiceStatus.isActive, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAuthServiceStatuses(refreshAllStatuses: boolean = false): Promise<void> {
        // always sort these to the top of the grid (in reverse order)
        this.apiSorting.add({ reference: 'flag.providerChangesRequested', direction: SortDirection.Descending });
        this.apiSorting.add({ reference: 'flag.isInitial', direction: SortDirection.Descending });
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.apiSorting.entries.length === 2 ? this.defaultApiSorting : this.apiSorting,
            includeInactive: this.includeInactive
        };

        const includes  = [
            'determination.status',
            'transitions'
        ];
        const fields = [
            'code',
            'description',
            'determination.isDetermination',
            'determination.status.determination.result',
            'flag.*',
            'transitions.description'
        ];

        try {
            this.state.beginLoad();
            const [response, allResponse] = await Promise.all([
                this.service.loadAuthServiceStatuses(options, includes, this.searchFilters, fields),
                refreshAllStatuses ? await this.service.loadAuthServiceStatuses({ all: true }, undefined, undefined, ['description']) : null
            ]);
            if(allResponse) this.allAuthServiceStatuses = allResponse.results;
            this.authServiceStatuses = this.setAuthServiceStatusDetails(<CustomAuthServiceStatus[]>response.results, allResponse?.count || 0);
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        const fields = [
            'description',
            'determination.result'
        ];

        const searchFilters: IAuthStatusSearchFilters = { isDetermination: true };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthStatuses({ all: true }, undefined, searchFilters, fields);
            const determinationNone: UM.AuthStatus = {
                description: this.i18n.tr("ui:labels.none"),
                code: "NONE",
                $display: this.i18n.tr("ui:labels.none"),
                determination: { isDetermination: true }
            } as UM.AuthStatus;
            this.determinationStatuses = [determinationNone, ...response.results];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
